/**
 * Admin Affiliate Data Fetching Hooks
 *
 * Custom hooks using React Query for listing users and toggling is_influencer.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createAffiliateUser,
  fetchAdminContainerVersions,
  fetchAffiliateUserCoupons,
  fetchAffiliateUsers,
  regenerateAffiliateUserPassword,
  sendAffiliateWelcomeEmail,
  setUserAffiliateFlag,
  setUserCommercialKind,
} from "../utils/adminApi";

export const AFFILIATES_QUERY_KEY_PREFIX = ["admin", "affiliates", "users"];

export const useAffiliateUsers = ({ q = "", only_affiliates = false, kind = "", kinds = [], limit = 50, enabled = true } = {}) => {
  const kindsKey = (Array.isArray(kinds) ? kinds : []).join(",");
  return useQuery({
    queryKey: [...AFFILIATES_QUERY_KEY_PREFIX, { q, only_affiliates, kind, kinds: kindsKey, limit }],
    queryFn: () => fetchAffiliateUsers({ q, only_affiliates, kind, kinds, limit }),
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useCreateAffiliateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      username,
      email,
      send_welcome_email,
      commercial_kind,
      coupons,
      default_coupon,
      default_access,
      access_grant,
    }) =>
      createAffiliateUser({
        username,
        email,
        send_welcome_email,
        commercial_kind,
        coupons,
        default_coupon,
        default_access,
        access_grant,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AFFILIATES_QUERY_KEY_PREFIX });
      await queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
    },
  });
};

export const useRegenerateAffiliatePassword = () => {
  return useMutation({
    mutationFn: (userId) => regenerateAffiliateUserPassword(userId),
  });
};

export const useSendAffiliateWelcomeEmail = () => {
  return useMutation({
    mutationFn: (userId) => sendAffiliateWelcomeEmail(userId),
  });
};

export const useSetAffiliateFlag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, is_affiliate }) => setUserAffiliateFlag(userId, is_affiliate),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AFFILIATES_QUERY_KEY_PREFIX });
    },
  });
};

export const useSetCommercialKind = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, commercial_kind }) => setUserCommercialKind(userId, commercial_kind),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AFFILIATES_QUERY_KEY_PREFIX });
    },
  });
};

export const useAffiliateUserCoupons = (userId, { enabled = true } = {}) => {
  return useQuery({
    queryKey: ["admin", "affiliates", "user_coupons", { userId }],
    queryFn: () => fetchAffiliateUserCoupons(userId),
    enabled: enabled && !!userId,
    refetchOnWindowFocus: false,
  });
};

export const useAdminContainerVersions = (containerId, { enabled = true } = {}) => {
  return useQuery({
    queryKey: ["admin", "containers", "versions", { containerId }],
    queryFn: () => fetchAdminContainerVersions(containerId),
    enabled: enabled && !!containerId,
    refetchOnWindowFocus: false,
  });
};
